import {
  type FrontendInstaller,
  otherInstalledIntegration,
} from "@p0security/integrations-installer";
import type { SalesforceAiComponents } from "@p0security/integrations-salesforce-ai-shared/types";
import type { ItemConfigOf } from "@p0security/shared/install/types";
import { CommandDisplay, Typography } from "@p0security/ui";
import type React from "react";

import {
  type InstallContext,
  awsStoreConsumerSecret,
  awsTf,
  gcpStoreConsumerSecret,
  gcpTf,
} from "./iac.ts";

type SecretManagerConfig = ItemConfigOf<
  SalesforceAiComponents["org"]
>["secretManager"];

type SecretManagerType = SecretManagerConfig["type"];

type InstallCommands = ReturnType<
  NonNullable<
    NonNullable<
      FrontendInstaller<SalesforceAiComponents>["components"]["org"]
    >["instructions"]
  >
>["commands"];

/** What a secret manager contributes to the org install steps. Methods, not
 * properties, let an implementation narrow to its own member. */
type OrgIac<T extends SecretManagerType = SecretManagerType> = {
  commands(
    context: InstallContext,
    org: string,
    secretManager: Extract<SecretManagerConfig, { type: T }>
  ): InstallCommands;
  /** The command that stores the consumer secret, if this secret manager has one */
  storeConsumerSecret(
    org: string,
    secretManager: Extract<SecretManagerConfig, { type: T }>
  ): string | undefined;
};

const gcpSmIac: OrgIac = {
  commands: (context, org, secretManager) => ({
    iac: [
      {
        header:
          'Apply the following terraform to deploy P0\'s Salesforce connector, then click "Next".',
        command: gcpTf(context, org, secretManager),
      },
    ],
  }),
  storeConsumerSecret: gcpStoreConsumerSecret,
} satisfies OrgIac<"gcp-sm">;

const awsSmIac: OrgIac = {
  commands: (context, org, secretManager) => ({
    iac: [
      {
        header:
          'Apply the following terraform to deploy P0\'s Salesforce connector, then click "Next".',
        command: awsTf(context, org, secretManager),
      },
    ],
  }),
  storeConsumerSecret: awsStoreConsumerSecret,
} satisfies OrgIac<"aws-sm">;

// The Azure Key Vault option is hidden, and the backend refuses it, until the
// connector runs on Azure.
const azureKvIac: OrgIac = {
  commands: () => ({}),
  storeConsumerSecret: () => undefined,
} satisfies OrgIac<"azure-kv">;

const orgIac: Record<SecretManagerType, OrgIac> = {
  "aws-sm": awsSmIac,
  "azure-kv": azureKvIac,
  "gcp-sm": gcpSmIac,
};

const { Paragraph } = Typography;

const SALESFORCE_AGENT_ACCESS_REPO_URL =
  "https://github.com/p0-security/p0-salesforce-agent-access";

const PackageSetup: React.FC = () => (
  <Paragraph>
    Agents get API-only Salesforce users that P0 creates and grants permission
    sets to. Before you continue, set up the P0 Salesforce package in your org.
    The package README gives the full steps.
    <ol className="preamble-list">
      <li>
        Make two users: a provisioner with API access and no admin permissions,
        and a different user for the token-exchange handler to run as.
      </li>
      <li>
        Log in to the org with the Salesforce CLI, and run <code>setup.sh</code>{" "}
        from{" "}
        <a
          href={SALESFORCE_AGENT_ACCESS_REPO_URL}
          target="_blank"
          rel="noreferrer"
        >
          P0&apos;s Salesforce agent access repository
        </a>
        . It deploys the package and the P0 Agent and P0 Provisioner apps, and
        prints the consumer keys that P0 asks for.
      </li>
      <li>
        Add a &quot;P0 Grantable Permission Set&quot; record for each permission
        set that agents may request.
      </li>
      <li>
        Turn on the MCP Service, and activate the hosted MCP servers that agents
        use.
      </li>
    </ol>
    P0&apos;s connector runs in your AWS account or Google Cloud project and
    holds the P0 Provisioner app&apos;s consumer secret, so P0 never sees it.
  </Paragraph>
);

const ConsumerSecretSetup: React.FC<{
  id: string;
  secretManager: SecretManagerConfig;
}> = ({ id, secretManager }) => {
  const storeConsumerSecret = orgIac[secretManager.type].storeConsumerSecret(
    id,
    secretManager
  );
  return (
    <div>
      {storeConsumerSecret && (
        <>
          <Paragraph type="secondary">
            Set <code>SF_CONSUMER_SECRET</code> to the P0 Provisioner app&apos;s
            consumer secret (Setup &gt; External Client App Manager), and run
            this command to store it. The connector cannot reach the org until
            the secret holds it.
          </Paragraph>
          <CommandDisplay commands={storeConsumerSecret} />
        </>
      )}
      <Paragraph type="secondary">
        Then enter the P0 Provisioner app&apos;s consumer key.
      </Paragraph>
    </div>
  );
};

export const salesforceAiInstaller: FrontendInstaller<SalesforceAiComponents> =
  {
    components: {
      org: {
        instructions: (context, id, item) => ({
          help: <PackageSetup />,
          commands: item?.secretManager
            ? orgIac[item.secretManager.type].commands(
                context,
                id,
                item.secretManager
              )
            : {},
        }),
        items: {
          provisionerConsumerKey: {
            labeler: (_, id, { secretManager, state }) =>
              state === "configure" && (
                <ConsumerSecretSetup id={id} secretManager={secretManager} />
              ),
          },
          secretManager: {
            options: {
              "aws-sm": {
                items: {
                  install: {
                    optionProvider: otherInstalledIntegration({
                      integration: "aws-sm",
                      component: "iam-write",
                    }),
                  },
                },
              },
              "gcp-sm": {
                items: {
                  install: {
                    optionProvider: otherInstalledIntegration({
                      integration: "gcp-sm",
                      component: "iam-write",
                    }),
                  },
                },
              },
            },
          },
        },
      },
    },
  };
